<template>
    <div class="survey-container">
        <div v-if="surveyCompleted" class="survey-completed mt-3">
            <b-col cols="12" class="mx-auto centered">
            <b-card>
                <base-icon width="100px" height="100px" src="/static-appopensalud/img/icons/full.png" color="blue" />
                <br>
                <h5 class="text-center mt-5">{{this.$i18n.t('surveys.completed2')}}</h5>
                <p v-if="fromDialog" class="text-center mt-5">{{this.$i18n.t('surveys.close-dialog')}}</p>
                <b-button
                    v-if="showOpenSaludButton"
                    variant="primary"
                    block
                    class="mt-5"
                    @click="goToOpenSalud"
                >{{this.$i18n.t('surveys.opensalud-go')}}
                </b-button>
            </b-card>
            </b-col>
        </div>

        <transition class="mt-3" name="fade" mode="out-in">
            <div v-if="!loading && !surveyCompleted && items.length > 0" class="survey-step">
								<h2 class="text-center mb-5" v-if="!started">{{ $i18nHelpers.normalize(surveyTitle) }}</h2>
                <div v-if="!started" class="text-center">
                    <b-button @click="handleSurveyStart" variant="primary">{{this.$i18n.t('surveys.start')}}</b-button>
                </div>
                <div v-else>
                    <div class="progress-container">
                            <span>{{ progressPercentage }}{{this.$i18n.t('surveys.percentage-completed')}}</span>
                            <b-progress class="progress-bar">
                                    <b-progress-bar
                                            :value="progressPercentage" 
                                            :max="100" 
                                            class="progress-bar-inner">
                                    </b-progress-bar>
                            </b-progress>
                    </div>
                    <question-card labelsEnabled v-model="questionResult" :question="question" :progress="answeredItems.size" :questionnaireLength="items.length" @answerQuestion="saveCurrentAnswer" @submitQuestionnaire="() => {}" fullWidthCard>
                            <template #additionalComment>
                                    <b-form-group class="mt-3">
                                            <b-form-textarea
                                                    v-if="responses[currentItem.id]"
                                                    v-model="responses[currentItem.id].freeText"
                                                    rows="2"
																										max-rows="4"
                                                    :placeholder="$i18n.t('surveys.comment-review')"
																										no-resize
                                            ></b-form-textarea>
                                    </b-form-group>
                            </template>
                    </question-card>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { BCard, BProgress, BProgressBar, BFormGroup, BFormTextarea, BButton } from 'bootstrap-vue'
import SurveyRepository from '@/repositories/SurveyRepository'
import i18nMixin from '@/mixins/i18n.mixin'
import QuestionCard from '@/components/utils/QuestionCard.vue'
import accountMixin from '@/mixins/account.mixin'
import LoadingAnim from '@/components/utils/LoadingAnim'

const I18N_FILE = 'public/views/survey'

const EVENTS = {
		SURVEY_COMPLETED: 'surveyCompleted',
		SURVEY_STEP_CHANGED: 'surveyStepChanged',
		SURVEY_STARTED: 'surveyStarted',
		SURVEY_LOADED: 'surveyLoaded'
}

export default {
    mixins: [i18nMixin(I18N_FILE), accountMixin],
    components: {
        BCard,
        BProgress,
        BProgressBar,
        BFormGroup,
        BFormTextarea,
        BButton,
        QuestionCard,
				LoadingAnim
    },
    props: {
        surveyTitle: { type: Object, required: true },
        surveyId: { type: String, required: true },
        items: { type: Array, required: true },
        fromDialog: { type: Boolean, required: false, default: false }
    },
    data () {
        return {
            currentIndex: 0,
            surveyCompleted: false,
            loading: true,
            responses: {},
            answeredItems: new Set(),
            ratingError: false,
            started: false
        }
    },
    computed: {
        userIsPatient () {
            return this.$store.getters['session/userIsPatient']
        },
        userIsCenter () {
            return this.$store.getters['session/userIsCenter']
        },
        userIsProfessional () {
            return this.$store.getters['session/userIsProfessional']
        },
        showOpenSaludButton () {
        return this.$route.path.includes('/survey/')
        },
        progressPercentage () {
            if (this.items.length === 0) return 0
            return Math.round(((this.answeredItems.size) / this.items.length) * 100)
        },
        currentItem () {
            return this.items.find(item => !this.answeredItems.has(item.id))
        },
        question: {
            get () {
                if (!this.currentItem) return null
                return {
                    question: this.$i18nHelpers.normalize(this.currentItem.description),
                    answer: [...Array(10)].map((_, i) => ({ value: i + 1 })),
                    result: this.responses[this.currentItem.id].rating
                }
            }
        },
        questionResult: {
            get () {
                return this.question ? this.question.result : null
            },
            set (newValue) {
                if (this.currentItem) {
                    this.$set(this.responses[this.currentItem.id], 'rating', newValue)
                }
            }
        }
    },
    watch: {
        items (newItems) {
            if (newItems.length > 0) {
                this.initializeResponses()
                this.setLastAnsweredItems()
            }
        },
        currentIndex () {
					this.$emit(EVENTS.SURVEY_STEP_CHANGED, this.currentIndex)
					this.$forceUpdate()
        },
				progressPercentage: async function (val) {
					this.$emit(EVENTS.SURVEY_STARTED)
					if (val === 100) {
						await this.submitSurvey()
					}
				}
    },
    async created () {
        await this.checkSurveyStatus()
        if (!this.surveyCompleted) {
            await this.setLastAnsweredItems()
            this.initializeResponses()
        }
        this.loading = false
				this.$emit(EVENTS.SURVEY_LOADED)
    },
    methods: {
        goToOpenSalud () {
            return this.goToSessionRoot()
        },
         async checkSurveyStatus () {
            try {
                const response = await SurveyRepository.checkSurveyCompleted(this.surveyId)
                this.surveyCompleted = response.data
            } catch (error) {
                this.$store.dispatch('showDangerAlert', this.$i18n.t('surveys.error.error-loading'))
            }
        },
        async setLastAnsweredItems () {
            try {
                const response = await SurveyRepository.getLastAnsweredItem(this.surveyId)
                this.answeredItems = new Set(response.data || [])
                this.currentIndex = this.answeredItems.size
            } catch (error) {
                this.$store.dispatch('showDangerAlert', this.$i18n.t('surveys.error.error-loading'))
            }
        },
        initializeResponses () {
            this.items.forEach(item => {
                if (!this.responses[item.id]) {
                    this.setItemResponses(item.id)
                }
            })
        },
        async saveCurrentAnswer () {
            const currentItem = this.currentItem
            if (!currentItem || !currentItem.id) return

            if (!this.responses[currentItem.id]) {
                this.setItemResponses(currentItem.id)
            }

            if (this.responses[currentItem.id].rating === null) {
                this.ratingError = true
                return
            }
            this.ratingError = false

            try {
                await SurveyRepository.postSurveyItemAnswer({
                    Rating: this.responses[currentItem.id].rating,
                    FreeText: this.responses[currentItem.id].freeText,
                    ItemId: currentItem.id,
                    SurveyId: this.surveyId
                })

                if (!this.answeredItems.has(currentItem.id)) {
                    this.answeredItems.add(currentItem.id)
                }

                this.setLastAnsweredItems()
            } catch (error) {
                this.$store.dispatch('showDangerAlert', this.$i18n.t('surveys.error.error-loading'))
            }
        },
        async submitSurvey () {
            await this.saveCurrentAnswer()
            if (this.answeredItems.size < this.items.length) {
                this.$store.dispatch('showDangerAlert', this.$i18n.t('surveys.info.error-finish'))
                return
            }

            try {
                await SurveyRepository.postSurveyAnswer(this.surveyId)
                this.surveyCompleted = true
                this.$emit(EVENTS.SURVEY_COMPLETED)
            } catch (error) {
                this.$store.dispatch('showDangerAlert', this.$i18n.t('surveys.error.error-send'))
            }
        },
        setItemResponses (itemId) {
            this.$set(this.responses, itemId, {rating: null, freeText: ''})
        },
				handleSurveyStart () {
					this.started = true
					this.currentIndex = 0
					this.$emit(EVENTS.SURVEY_STARTED)
				}
    }
}
</script>

<style scoped lang="scss">
 .survey-container {
    display: contents;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}
/deep/ .neu-card-sv {
  box-shadow: $custom-shadow;
  border: 1px solid $white;
  border-radius: 23px;
  display: block;
  background-color: $white;
  -webkit-transition: all .5s ease;
  transition: all .5s ease;
  text-align: center;
  margin-top: auto;
  margin-bottom: auto;
  padding: 2rem;
}
.progress-container {
    width: 100%;
    text-align: center;
    margin-bottom: 2rem;
}
.progress-bar {
    height: 5px;
    background-color: #ccc !important;
}
.progress-bar-inner {
    transition: width 0.5s ease-in-out;
    background-color: #2d2df5 !important;
}
.survey-step {
    width: 100%;
}
.survey-completed {
    text-align: center;
    padding: 20px;
}
.loading-container {
    text-align: center;
    padding: 20px;
}

.error-text {
    color: $red;
    font-size: 0.9rem;
    font-weight: bold;
    margin-top: 5px;
}
</style>



// WEBPACK FOOTER //
// src/components/views/surveys/SurveyTemplate.vue